import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import type {
  AuthOrg,
  AuthResponse,
  AuthUser,
  AuthWorkspace,
} from '@/lib/auth';
import { tokenStorage } from '@/lib/storage-tokens';

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  organization: AuthOrg | null;
  workspace: AuthWorkspace | null;
  setAuth: (auth: AuthResponse) => void;
  clear: () => void;
}

/**
 * Back-compat storage adapter.
 *
 * `lib/api.ts` (the fetch wrapper) reads/writes the `access_token` and
 * `refresh_token` keys through `tokenStorage` (`lib/storage-tokens.ts`) to
 * attach Bearer tokens to outgoing requests and to drive the 401-refresh
 * queue. `tokenStorage` is `sessionStorage`-primary — each tab pins its own
 * session — bootstrapping once from `localStorage` on first read and
 * mirroring writes to both stores, so pre-existing logged-in users still
 * have their tokens picked up.
 *
 * We don't want to invalidate their sessions or duplicate the data into a new
 * JSON blob (`trulyinbox-auth`). So instead of giving Zustand its own
 * persistence key, we wire a custom adapter that splits Zustand's persisted
 * payload back into the legacy `access_token` / `refresh_token` / `user` /
 * `org` / `workspace` keys (via `tokenStorage`) on `setItem`, and reassembles
 * them on `getItem`.
 *
 * Net effect:
 *  - `lib/api.ts` keeps working via `tokenStorage.get('access_token')`.
 *  - Existing sessions hydrate into the store on first read.
 *  - Every later mutation goes through `useAuthStore` (via `storeAuth`/
 *    `clearAuth`), which writes back to the same legacy keys through
 *    `tokenStorage`.
 */
const writeOrRemove = (key: 'access_token' | 'refresh_token', value: string | null): void => {
  if (value) tokenStorage.set(key, value);
  else tokenStorage.remove(key);
};

const writeJsonOrRemove = (key: 'user' | 'org' | 'workspace', value: unknown): void => {
  if (value) tokenStorage.set(key, JSON.stringify(value));
  else tokenStorage.remove(key);
};

const splitLocalStorage: StateStorage = {
  getItem: (): string | null => {
    const accessToken = tokenStorage.get('access_token');
    const refreshToken = tokenStorage.get('refresh_token');
    const userRaw = tokenStorage.get('user');
    const orgRaw = tokenStorage.get('org');
    const workspaceRaw = tokenStorage.get('workspace');

    if (!accessToken && !refreshToken && !userRaw && !orgRaw && !workspaceRaw) {
      return null;
    }

    const parse = <T>(raw: string | null): T | null => {
      if (!raw) return null;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return null;
      }
    };

    return JSON.stringify({
      state: {
        accessToken,
        refreshToken,
        user: parse<AuthUser>(userRaw),
        organization: parse<AuthOrg>(orgRaw),
        workspace: parse<AuthWorkspace>(workspaceRaw),
      },
      version: 0,
    });
  },
  setItem: (_key, value): void => {
    const parsed = JSON.parse(value) as {
      state: Pick<
        AuthState,
        'accessToken' | 'refreshToken' | 'user' | 'organization' | 'workspace'
      >;
    };
    const s = parsed.state;
    writeOrRemove('access_token', s.accessToken);
    writeOrRemove('refresh_token', s.refreshToken);
    writeJsonOrRemove('user', s.user);
    writeJsonOrRemove('org', s.organization);
    writeJsonOrRemove('workspace', s.workspace);
  },
  removeItem: (): void => {
    tokenStorage.clearAll();
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      organization: null,
      workspace: null,
      setAuth: (auth) =>
        set({
          accessToken: auth.accessToken,
          refreshToken: auth.refreshToken,
          user: auth.user,
          organization: auth.organization,
          workspace: auth.workspace,
        }),
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          organization: null,
          workspace: null,
        }),
    }),
    {
      // The legacy keys live under a synthetic name — the adapter ignores it
      // and reads/writes the individual `access_token` / `user` / etc. keys.
      name: 'trulyinbox-auth',
      storage: createJSONStorage(() => splitLocalStorage),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        organization: s.organization,
        workspace: s.workspace,
      }),
    },
  ),
);
